import { Message, MessageContent, MessageResponse } from '@/components/ai-elements/message'
import { Reasoning, ReasoningContent, ReasoningTrigger } from '@/components/ai-elements/reasoning'
import { Source, Sources, SourcesContent, SourcesTrigger } from '@/components/ai-elements/sources'
import { Tool, ToolContent, ToolHeader, ToolOutput } from '@/components/ai-elements/tool'
import { Button } from '@/components/ui/button'
import { getReasoningText } from '@/lib'
import { cn } from '@/lib/utils'
import { Jobibot } from '@/ui/rapid-ui'
import { useChatCancelToolCall } from '@jobilla/nexus-sdk'
import { getToolName, isStaticToolUIPart } from 'ai'
import type { ToolUIPart, UIMessage } from 'ai'
import { X } from 'lucide-react'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Delay before showing cancel button (in milliseconds)
// This prevents premature cancellation before jobs have a chance to check status
const CANCEL_BUTTON_DELAY_MS = 2 * 60 * 1000 // 2 minutes

const isReadOnlyToolName = (toolName: string): boolean => toolName.startsWith('get')

// UIMessage leaves metadata untyped, so this narrows rather than asserts.
const hasFailedStatus = (metadata: unknown): boolean => {
	return typeof metadata === 'object' && metadata !== null && 'status' in metadata
		&& metadata.status === 'failed'
}

type ToolWithCancelProps = {
	chatId: string
	toolCallId: string
	toolName: string
	state: ToolUIPart['state']
	isPending: boolean
	showOutput: boolean
	part: ToolUIPart
	toolSpecs: Record<string, { label: string; icon?: any }>
}

const ToolWithCancel = (
	{ chatId, toolCallId, toolName, state, isPending, showOutput, part, toolSpecs }: ToolWithCancelProps,
) => {
	const { t } = useTranslation('common')
	const { mutate: cancelTool, isPending: isCancelling } = useChatCancelToolCall()

	// Track elapsed time to only show cancel button after delay
	const [showCancel, setShowCancel] = useState(false)
	const [startTime] = useState(() => Date.now())
	const [isOpen, setIsOpen] = useState(state === 'output-error')

	useEffect(() => {
		if (state === 'output-error') {
			setIsOpen(true)
		}
	}, [state])

	useEffect(() => {
		if (!isPending) {
			setShowCancel(false)
			return
		}

		const checkElapsedTime = () => {
			const elapsed = Date.now() - startTime
			if (elapsed >= CANCEL_BUTTON_DELAY_MS) {
				setShowCancel(true)
			}
		}

		// Check immediately
		checkElapsedTime()

		// Then check every second
		const interval = setInterval(checkElapsedTime, 1000)

		return () => clearInterval(interval)
	}, [isPending, startTime])

	return (
		<div className="relative grow">
			<Tool className="max-w-[80%]" open={isOpen} onOpenChange={setIsOpen}>
				<ToolHeader
					type={part.type}
					state={state}
					title={toolSpecs[toolName as keyof typeof toolSpecs]?.label || toolName}
					icon={toolSpecs[toolName as keyof typeof toolSpecs]?.icon}
					showChevron={showOutput}
				/>
				<ToolContent>
					{showOutput && (
						<ToolOutput
							output={part.output}
							errorText={part.errorText}
						/>
					)}
				</ToolContent>
			</Tool>
			{isPending && toolCallId && showCancel && (
				<Button
					variant="ghost"
					size="sm"
					className="absolute top-2 right-2 h-6 px-2 text-xs"
					onClick={() => cancelTool({ chat: chatId, toolCallId })}
					disabled={isCancelling}
					title={t('editor.chat.cancelTooltip')}
				>
					<X className="h-3 w-3 mr-1" />
					{isCancelling ? t('editor.chat.cancelling') : t('editor.chat.cancel')}
				</Button>
			)}
		</div>
	)
}

export type ChatMessageProps = {
	message: UIMessage
	toolSpecs: Record<string, { label: string; icon?: any }>
	isLastMessage: boolean
	isStreaming: boolean
	chatId: string
}

const ChatMessageComponent = ({ message, toolSpecs, isLastMessage, isStreaming, chatId }: ChatMessageProps) => {
	const { t } = useTranslation('common')
	const isFailed = message.role === 'assistant' && hasFailedStatus(message.metadata)
	const sourceUrlParts = message.role === 'assistant'
		? message.parts.filter((part): part is Extract<typeof part, { type: 'source-url'; url: string }> =>
			part.type === 'source-url'
		)
		: []
	const isLoading = (message?.parts?.length === 0 || message?.parts?.at(-1)?.type === 'step-start')

	return (
		<div className={cn('chat-message-container max-w-full flex flex-row', isFailed && 'opacity-80')}>
			{(!isLoading || isFailed) && (
				<div className="mr-2 mt-2 shrink-0">{message.role === 'assistant' && <Jobibot size={18} />}</div>
			)}
			<div className="grow min-w-0 flex flex-col">
				{isFailed && (
					<Message from="assistant">
						<MessageContent>
							<div className="text-destructive/80 text-sm">
								{t('editor.chat.failedResponse')}
							</div>
						</MessageContent>
					</Message>
				)}
				{sourceUrlParts.length > 0 && (
					<Sources>
						<SourcesTrigger count={sourceUrlParts.length} />
						{sourceUrlParts.map((part, i) => (
							<SourcesContent key={`${message.id}-source-${i}`}>
								<Source
									href={part.url}
									title={part.url}
								/>
							</SourcesContent>
						))}
					</Sources>
				)}
				{message.parts.map((part, i) => {
					switch (part.type) {
						case 'text':
							return (
								<Message key={`${message.id}-text-${i}`} from={message.role}>
									<MessageContent>
										<MessageResponse>
											{part.text}
										</MessageResponse>
									</MessageContent>
								</Message>
							)
						case 'reasoning':
							// Current AI SDK reasoning parts use `text`; keep `content` as legacy fallback.
							const reasoningText = getReasoningText(part)
							return (
								<Reasoning
									key={`${message.id}-reasoning-${i}`}
									className="w-full"
									isStreaming={isStreaming
										&& isLastMessage
										&& i === message.parts.length - 1}
								>
									<ReasoningTrigger />
									<ReasoningContent>{reasoningText}</ReasoningContent>
								</Reasoning>
							)
						case 'tool-result':
							return null
						default: {
							if (isStaticToolUIPart(part)) {
								const toolName = getToolName(part)
								const state = part.state
								const showOutput = (state === 'output-error' && Boolean(part.errorText))
									|| (state === 'output-available' && isReadOnlyToolName(toolName))
								const isPending = state !== 'output-available' && state !== 'output-error'
								const toolCallId = part.toolCallId

								return (
									<div key={`${message.id}-tool-wrapper-${i}`} className="flex flex-row w-full">
										<ToolWithCancel
											key={`${message.id}-tool-${i}`}
											chatId={chatId}
											toolCallId={toolCallId}
											toolName={toolName}
											state={state as ToolUIPart['state']}
											isPending={isPending}
											showOutput={showOutput}
											part={part}
											toolSpecs={toolSpecs}
										/>
									</div>
								)
							}
							return null
						}
					}
				})}
			</div>
		</div>
	)
}

// Custom comparison function for memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps: ChatMessageProps, nextProps: ChatMessageProps) => {
	// Re-render if message object changed
	if (prevProps.message !== nextProps.message) {
		return false
	}

	// Re-render if this became/stopped being the last message
	if (prevProps.isLastMessage !== nextProps.isLastMessage) {
		return false
	}

	// Re-render if streaming state changed (affects last message's reasoning indicator)
	if (prevProps.isStreaming !== nextProps.isStreaming) {
		return false
	}

	// toolSpecs is static, no need to check
	return true
}

export const ChatMessage = memo(ChatMessageComponent, arePropsEqual)
ChatMessage.displayName = 'ChatMessage'
